/*!
 * Attaches GA cookie data to Snowplow events v4.6.9 (http://bit.ly/sp-js)
 * Copyright 2022 Snowplow Analytics Ltd, 2010 Anthon Pang
 * Licensed under BSD-3-Clause
 */

import { __assign, __spreadArray } from 'tslib';
import { cookie } from '@snowplow/browser-tracker-core';

var UNIVERSAL_ANALYTICS_COOKIES_SCHEMA = 'iglu:com.google.analytics/cookies/jsonschema/1-0-0';
var GOOGLE_ANALYTICS_4_COOKIES_SCHEMA = 'iglu:com.google.ga4/cookies/jsonschema/1-0-0';

var defaultPluginOptions = {
    ua: false,
    ga4: true,
    ga4MeasurementId: '',
    cookiePrefix: [],
};
/**
 * Captures the GA cookies on a page and sends as context on each event
 * @param pluginOptions.ua - Send Universal Analytics specific cookie values.
 * @param pluginOptions.ga4 - Send Google Analytics 4 specific cookie values.
 * @param pluginOptions.ga4MeasurementId - Measurement id/ids to search the Google Analytics 4 session cookie. Can be a single measurement id as a string or an array of measurement id strings. The cookie has the form of _ga_<container-id> where <container-id> is the data stream container id.
 * @param pluginOptions.cookiePrefix - Cookie prefix set on the Google Analytics 4 cookies using the cookie_prefix option of the gtag.js tracker.
 */
function GaCookiesPlugin(pluginOptions) {
    if (pluginOptions === void 0) { pluginOptions = defaultPluginOptions; }
    return {
        contexts: function () {
            var contexts = [];
            var _a = __assign(__assign({}, defaultPluginOptions), pluginOptions), ga4 = _a.ga4, ga4MeasurementId = _a.ga4MeasurementId, ua = _a.ua, cookiePrefix = _a.cookiePrefix;
            var GA_USER_COOKIE = '_ga';
            var GA4_MEASUREMENT_ID_PREFIX = 'G-';
            var GA4_COOKIE_PREFIX = '_ga_';
            if (ua) {
                var uaCookiesContext_1 = {
                    schema: UNIVERSAL_ANALYTICS_COOKIES_SCHEMA,
                    data: {},
                };
                ['__utma', '__utmb', '__utmc', '__utmv', '__utmz', GA_USER_COOKIE].forEach(function (cookieType) {
                    var value = cookie(cookieType);
                    if (value) {
                        uaCookiesContext_1.data[cookieType] = value;
                    }
                });
                contexts.push(uaCookiesContext_1);
            }
            if (ga4) {
                var cookiePrefixes = Array.isArray(cookiePrefix) ? __spreadArray([], cookiePrefix, true) : [cookiePrefix];
                /* We also will search for the default (no prefix) in every case. */
                cookiePrefixes.unshift('');
                cookiePrefixes.forEach(function (prefix) {
                    var userCookie = cookie(prefix + GA_USER_COOKIE);
                    var sessionCookies = [];
                    if (ga4MeasurementId) {
                        var measurementIdentifiers = Array.isArray(ga4MeasurementId) ? __spreadArray([], ga4MeasurementId, true) : [ga4MeasurementId];
                        measurementIdentifiers.forEach(function (cookieIdentifier) {
                            var sessionCookieValue = cookie(prefix + cookieIdentifier.replace(GA4_MEASUREMENT_ID_PREFIX, GA4_COOKIE_PREFIX));
                            if (sessionCookieValue) {
                                sessionCookies.push({
                                    measurement_id: cookieIdentifier,
                                    session_cookie: sessionCookieValue,
                                });
                            }
                        });
                    }
                    if (!userCookie && !sessionCookies.length) {
                        return;
                    }
                    var ga4CookiesContext = {
                        schema: GOOGLE_ANALYTICS_4_COOKIES_SCHEMA,
                        data: {
                            _ga: userCookie,
                            session_cookies: sessionCookies.length ? sessionCookies : undefined,
                            cookie_prefix: prefix || undefined,
                        },
                    };
                    contexts.push(ga4CookiesContext);
                });
            }
            return contexts;
        },
    };
}

export { GaCookiesPlugin };
//# sourceMappingURL=index.module.js.map
